import http from '../http'

/**
 * 分页查询客户合同信息
 * @param {Object} params 查询参数
 * @param {String} [params.contract_name] - 合同名称(可选，支持模糊查询)
 * @param {String} [params.status] - 合同状态(可选)
 * @param {Number} [params.current] - 当前页码(可选，默认为1)
 * @param {Number} [params.pageSize] - 每页记录数(可选，默认为10)
 * @returns {Promise} 返回客户合同信息列表和分页信息
 */
export function query_customer_contracts_pagination(params = {}) {
return http.get('/customer-contracts', { params })
}

/**
 * 根据ID查询客户合同详情
 * @param {number} id - 客户合同ID
 * @param {Object} [params] - 可选参数
 * @param {string} [params.contract_type] - 合同类型
 * @returns {Promise} - 返回客户合同详情的Promise对象
 */
export function query_customer_contract_by_id(id, params = {}) {
return http.get(`/customer-contracts/${id}`, { params });
}

/**
 * 分页查询供应商合同信息
 * @param {Object} params 查询参数
 * @param {String} [params.contract_name] - 合同名称(可选，支持模糊查询)
 * @param {Number} [params.supplier_id] - 供应商ID(可选，精确匹配)
 * @param {Number} [params.status] - 合同状态(可选，精确匹配，数字类型，如0='待审核', 1='已审核'等)
 * @param {String} [params.delivery_start] - 交付开始时间(可选，格式为YYYY-MM-DD)
 * @param {String} [params.delivery_end] - 交付结束时间(可选，格式为YYYY-MM-DD)
 * @param {Number} [params.page] - 当前页码(可选，用于分页查询，默认为1)
 * @param {Number} [params.pageSize] - 每页记录数(可选，用于分页查询，默认为10)
 * @returns {Promise} 返回合同信息列表和分页信息
 */
export function query_supplier_contracts(params = {}) {
  return http.get('/supplier-contracts', { params })
}

/**
 * 获取单个供应商合同的详细信息
 * @param {Number|String} id - 供应商合同ID
 * @param {Object} [params] - 可选参数
 * @param {string} [params.contract_type] - 合同类型
 * @returns {Promise} 返回合同详细信息，包括关联的产品明细和变动利率
 * @description 当合同的fixed_rate为0时，返回结果将包含variable_rates数组，包含适用于该合同的变动利率数据
 */
export function query_supplier_contract_by_id(id, params = {}) {
return http.get(`/supplier-contracts/${id}`, { params });
}

/**
 * 保存（新增或更新）供应商合同信息
 * @param {Object} data 合同信息数据
 * @param {Number} [data.id] - 合同ID(更新时必填，新增时不填)
 * @param {String} data.contract_name - 合同名称(必填)
 * @param {Number} data.supplier_id - 供应商ID(必填)
 * @param {Number} [data.create_id] - 创建者单位ID
 * @param {String} data.delivery_time - 交付时间(必填，格式为ISO日期字符串)
 * @param {String} data.pricing_method - 计价方式(必填，如'固定价格')
 * @param {String} data.is_measured - 是否计息(必填，如'是'或'否')
 * @param {String} data.settlement_method - 结算方式(必填，如'按月结算')
 * @param {Number} data.payment1_daynum - 付款方式1天数
 * @param {Number} data.payment2_daynum - 付款方式2天数
 * @param {Number} data.interestfree_maxday - 免息天数
 * @param {Number} data.fixed_rate - 固定利率(0-100之间)
 * @param {String} [data.status] - 状态(如'待审核'，'已审核'等)
 * @param {Array} [data.contract_list] - 材料明细列表
 * @param {Array} [data.variable_rates] - 变动利率列表(仅当fixed_rate为0时需要)
 * @returns {Promise} 返回保存结果
 */
export function save_supplier_contract(data) {
return http.post('/supplier-contracts', data)
}

/**
 * 随机创建一个固定利率的供应商合同
 * @description 自动创建一个包含随机数据的固定利率供应商合同
 * @returns {Promise} 返回保存结果
 */
export async function create_random_fixed_rate_contract() {
  try {
// 生成随机测试数据
    const contractData = {
      contract_name: `固定利率合同_${new Date().getTime()}`,
      supplier_id: Math.floor(Math.random() * 5) + 1, // 随机选择1-5之间的供应商ID
      create_id: Math.floor(Math.random() * 3) + 1, // 随机选择1-3之间的创建者单位ID
      delivery_time: new Date(new Date().setMonth(new Date().getMonth() + 2)).toISOString(),
      pricing_method: "固定价格",
      is_measured: "是",
      settlement_method: "按月结算",
      payment1_daynum: 30,
      payment2_daynum: 60,
      interestfree_maxday: 10,
      fixed_rate: (Math.random() * 5 + 0.5).toFixed(2), // 随机生成0.5-5.5之间的固定利率
      status: "待审核",
      contract_list: [
        {
          category: "原材料",
          materials_name: "测试材料1",
          materials_spec: "规格型号1",
          unit: "吨",
          quantity: Math.floor(Math.random() * 100) + 50, // 随机数量50-150
          before_price: 1000,
          tax_rate: 0.13,
          price: 1130,
          supplier_name: "测试供应商"
        }
      ]
    }
    
// 修改简化数据，确保包含合同清单项
    const simplifiedData = {
      contract_name: contractData.contract_name,
      supplier_id: contractData.supplier_id,
      create_id: contractData.create_id, // 添加创建者单位ID
      delivery_time: contractData.delivery_time,
      pricing_method: contractData.pricing_method,
      is_measured: contractData.is_measured,
      settlement_method: contractData.settlement_method,
      payment1_daynum: contractData.payment1_daynum,
      payment2_daynum: contractData.payment2_daynum,
      interestfree_maxday: contractData.interestfree_maxday,
      fixed_rate: contractData.fixed_rate,
      status: contractData.status,
      contract_list: contractData.contract_list // 确保包含合同清单项
    }
    
// 保存合同，使用简化数据
    try {
      const response = await save_supplier_contract(simplifiedData)
return response
    } catch (saveError) {
      console.error('直接调用save_supplier_contract失败:', saveError)
      // 尝试直接使用http模块
return http.post('/supplier-contracts', simplifiedData)
    }
  } catch (error) {
    console.error('创建固定利率合同失败:', error)
    return Promise.reject({
      data: {
        success: false,
        message: '创建固定利率合同失败: ' + (error.message || '未知错误'),
        error: error.toString(),
        stack: error.stack
      }
    })
  }
}

/**
 * 随机创建一个变动利率的供应商合同
 * @description 自动创建一个包含随机数据和变动利率的供应商合同
 * @returns {Promise} 返回保存结果
 */
export async function create_random_variable_rate_contract() {
  try {
// 生成随机测试数据
    const contractData = {
      contract_name: `变动利率合同_${new Date().getTime()}`,
      supplier_id: Math.floor(Math.random() * 5) + 1, // 随机选择1-5之间的供应商ID
      create_id: Math.floor(Math.random() * 3) + 1, // 随机选择1-3之间的创建者单位ID
      delivery_time: new Date(new Date().setMonth(new Date().getMonth() + 2)).toISOString(),
      pricing_method: "固定价格",
      is_measured: "是",
      settlement_method: "按月结算",
      payment1_daynum: 30,
      payment2_daynum: 60,
      interestfree_maxday: 10,
      fixed_rate: 0, // 固定利率为0表示使用变动利率
      status: "待审核",
      contract_list: [
        {
          category: "原材料",
          materials_name: "测试材料A",
          materials_spec: "规格型号A",
          unit: "个",
          quantity: Math.floor(Math.random() * 1000) + 100, // 随机数量100-1100
          before_price: 200,
          tax_rate: 0.09,
          price: 218,
          supplier_name: "测试供应商"
        }
      ],
      variable_rates: [
        {
          date_max: 30,
          rate: (Math.random() * 2 + 1).toFixed(2) // 随机生成1-3之间的利率
        },
        {
          date_max: 60,
          rate: (Math.random() * 2 + 2).toFixed(2) // 随机生成2-4之间的利率
        },
        {
          date_max: 90,
          rate: (Math.random() * 2 + 3).toFixed(2) // 随机生成3-5之间的利率
        }
      ]
    }
    
// 修改简化数据，确保包含合同清单项和变动利率
    const finalData = {
      contract_name: contractData.contract_name,
      supplier_id: contractData.supplier_id,
      create_id: contractData.create_id, // 添加创建者单位ID
      delivery_time: contractData.delivery_time,
      pricing_method: contractData.pricing_method,
      is_measured: contractData.is_measured,
      settlement_method: contractData.settlement_method,
      payment1_daynum: contractData.payment1_daynum,
      payment2_daynum: contractData.payment2_daynum,
      interestfree_maxday: contractData.interestfree_maxday,
      fixed_rate: 0,
      status: contractData.status,
      contract_list: contractData.contract_list, // 仅使用contract_list字段
      variable_rates: contractData.variable_rates
    }
    
// 保存合同，使用最终数据
    try {
      const response = await save_supplier_contract(finalData)
return response
    } catch (saveError) {
      console.error('直接调用save_supplier_contract失败:', saveError)
      // 尝试直接使用http模块
return http.post('/supplier-contracts', finalData)
    }
  } catch (error) {
    console.error('创建变动利率合同失败:', error)
    return Promise.reject({
      data: {
        success: false,
        message: '创建变动利率合同失败: ' + (error.message || '未知错误'),
        error: error.toString(),
        stack: error.stack
      }
    })
  }
}

/**
 * 随机更新一个供应商合同
 * @description 从现有合同中随机选择一个，并根据其是固定利率还是变动利率进行相应的更新
 * @returns {Promise} 返回更新结果
 */
export async function update_random_contract() {
  try {
    // 1. 获取所有供应商合同
const contractsResponse = await query_supplier_contracts()
// 2. 从响应中提取合同列表
    let contractsList = []
    
    // 尝试各种可能的数据结构
    try {
      if (contractsResponse.data && contractsResponse.data.data && Array.isArray(contractsResponse.data.data.data)) {
contractsList = contractsResponse.data.data.data
      } else if (contractsResponse.data && contractsResponse.data.data && Array.isArray(contractsResponse.data.data)) {
contractsList = contractsResponse.data.data
      } else if (contractsResponse.data && Array.isArray(contractsResponse.data.items)) {
contractsList = contractsResponse.data.items
      } else if (Array.isArray(contractsResponse.data)) {
contractsList = contractsResponse.data
      } else {
        // 如果没有找到有效数据，创建一个测试合同再更新
const newContract = await create_random_fixed_rate_contract()
        if (newContract.data && newContract.data.data) {
          contractsList = [newContract.data.data]
        } else if (newContract.data) {
          contractsList = [newContract.data]
        } else {
          throw new Error('无法创建测试合同')
        }
      }
    } catch (parseError) {
      console.error('解析合同数据出错:', parseError)
      throw parseError
    }
    
// 3. 确保有可用的合同
    if (!contractsList || !contractsList.length) {
      console.error('没有可用的合同信息')
      return Promise.reject({
        data: {
          success: false,
          message: '没有可用的合同信息，无法进行更新',
          details: contractsResponse && contractsResponse.data ? JSON.stringify(contractsResponse.data) : '无响应数据'
        }
      })
    }
    
    // 4. 随机选择一个合同
    const randomContract = contractsList[Math.floor(Math.random() * contractsList.length)]
// 5. 检查是固定利率还是变动利率合同
    const isFixedRate = randomContract.fixed_rate !== 0 && randomContract.fixed_rate !== '0'
// 6. 根据合同类型构建更新数据
    const updateData = {
      id: randomContract.id,
      contract_name: randomContract.contract_name + '_已更新',
      supplier_id: randomContract.supplier_id,
      delivery_time: randomContract.delivery_time,
      pricing_method: randomContract.pricing_method,
      is_measured: randomContract.is_measured,
      settlement_method: randomContract.settlement_method,
      payment1_daynum: randomContract.payment1_daynum,
      payment2_daynum: randomContract.payment2_daynum,
      interestfree_maxday: randomContract.interestfree_maxday,
      status: randomContract.status
    }
    
    // 根据合同类型更新相应的利率数据
    if (isFixedRate) {
      // 如果是固定利率合同，随机更新固定利率
      updateData.fixed_rate = (parseFloat(randomContract.fixed_rate) + Math.random()).toFixed(2)
    } else {
      // 如果是变动利率合同，保持fixed_rate为0，更新变动利率
      updateData.fixed_rate = 0
      
      // 提取或创建变动利率数组
      let variableRates = []
      if (randomContract.variable_rates && Array.isArray(randomContract.variable_rates)) {
        variableRates = [...randomContract.variable_rates]
      } else {
        // 如果没有变动利率数据，创建新的
        variableRates = [
          { date_max: 30, rate: "1.5" },
          { date_max: 60, rate: "2.5" },
          { date_max: 90, rate: "3.5" }
        ]
      }
      
      // 随机更新变动利率
      variableRates = variableRates.map(item => ({
        ...item,
        rate: (parseFloat(item.rate) + Math.random()).toFixed(2)
      }))
      
      updateData.variable_rates = variableRates
    }
    
// 7. 更新合同
    return save_supplier_contract(updateData)
  } catch (error) {
    console.error('更新随机合同失败:', error)
    return Promise.reject({
      data: {
        success: false,
        message: '更新合同失败: ' + (error.message || '未知错误'),
        error: error.toString(),
        stack: error.stack
      }
    })
  }
}

/**
 * 删除指定ID的供应商合同
 * @param {Number|String} id - 供应商合同ID
 * @returns {Promise} 返回删除结果
 * @description 软删除操作，将合同的deleted字段标记为true
 */
export function del_contract_by_id(id) {
return http.delete(`/contracts/${id}`)
}

export function del_supplier_contract_by_id(id) {
return http.delete(`/supplier-contracts/${id}`)
}

/**
 * 保存（新增或更新）客户合同信息
 * @param {Object} data 客户合同信息数据
 * @param {Number} [data.id] - 合同ID(更新时必填，新增时不填)
 * @param {String} data.contract_name - 合同名称(必填)
 * @param {String} data.isMeasured - 是否计息(如'Y'或'N')
 * @param {String} data.interestfree_maxday - 免息天数
 * @param {String} data.fixedRate - 固定利率(0表示使用变动利率)
 * @param {String} data.settlement_method - 结算方式(如'按月结算')
 * @param {String} data.payment_method - 付款方式(如'按月付款')
 * @param {String} data.price_method - 计价方式(如'实际计量')
 * @param {String} data.status - 状态(如'进行中')
 * @param {Array} [data.materials] - 材料清单列表
 * @param {Array} [data.customers] - 客户项目关系列表
 * @param {Array} [data.variable_rates] - 变动利率列表(仅当fixedRate为0时需要)
 * @returns {Promise} 返回保存结果
 */
export function save_customer_contract(data) {
return http.post('/customer-contracts', data)
}

/**
 * 随机创建一个固定利率的客户合同
 * @description 自动创建一个包含随机数据的固定利率客户合同
 * @returns {Promise} 返回保存结果
 */
export async function create_random_fixed_rate_customer_contract() {
  try {
// 生成随机测试数据
    const contractData = {
      contract_name: `客户固定利率合同_${new Date().getTime()}`,
      is_measured: "是",
      interestfree_maxday: "30",
      fixed_rate: (Math.random() * 5 + 0.5).toFixed(2),
      create_id: Math.floor(Math.random() * 3) + 1,
      settlement_method: "按月结算",
      payment_method: "按月付款",
      pricing_method: "实际计量",
      payment1_daynum: 30,
      payment2_daynum: 60,
      status: "进行中",
      materials: [
        {
          category: "钢材",
          materials_name: "螺纹钢",
          materials_spec: "Φ16",
          unit: "吨",
          quantity: Math.floor(Math.random() * 50) + 10,
          before_price: "4000",
          tax_rate: "0.09",
          price: "4360",
          supplier_name: "测试供应商"
        }
      ],
      customers: [
        {
          customer_id: Math.floor(Math.random() * 5) + 1,
          project_id: Math.floor(Math.random() * 3) + 1
        }
      ]
    }
    
// 创建最终提交数据，确保字段名称符合后端要求
    const finalData = {
      ...contractData,
      createId: contractData.create_id,
      isMeasured: contractData.is_measured,
      fixedRate: contractData.fixed_rate,
      price_method: contractData.pricing_method
    }
    
// 保存合同
    try {
      const response = await save_customer_contract(finalData)
return response
    } catch (error) {
      console.error('创建固定利率客户合同失败:', error)
      
      // 尝试直接使用http模块作为备选方案
try {
        const directResponse = await http.post('/supplier-contracts', finalData)
return directResponse
      } catch (httpError) {
        console.error('直接HTTP请求也失败:', httpError)
        throw httpError
      }
    }
  } catch (error) {
    console.error('创建固定利率客户合同失败:', error)
    return Promise.reject({
      data: {
        success: false,
        message: '创建固定利率客户合同失败: ' + (error.message || '未知错误'),
        error: error.toString(),
        stack: error.stack
      }
    })
  }
}

/**
 * 随机创建一个变动利率的客户合同
 * @description 自动创建一个包含随机数据和变动利率的客户合同
 * @returns {Promise} 返回保存结果
 */
export async function create_random_variable_rate_customer_contract() {
  try {
// 生成随机测试数据
    const contractData = {
      contract_name: `客户变动利率合同_${new Date().getTime()}`,
      is_measured: "是",
      interestfree_maxday: "30",
      fixed_rate: "0",
      create_id: Math.floor(Math.random() * 3) + 1,
      settlement_method: "按月结算",
      payment_method: "按月付款",
      pricing_method: "实际计量",
      payment1_daynum: 30,
      payment2_daynum: 60,
      status: "进行中",
      materials: [
        {
          category: "水泥",
          materials_name: "通用水泥",
          materials_spec: "P.O42.5",
          unit: "吨",
          quantity: Math.floor(Math.random() * 100) + 20,
          before_price: "500",
          tax_rate: "0.09",
          price: "545",
          supplier_name: "测试供应商2"
        },
        {
          category: "砂石",
          materials_name: "细砂",
          materials_spec: "标准",
          unit: "方",
          quantity: Math.floor(Math.random() * 200) + 50,
          before_price: "200",
          tax_rate: "0.09",
          price: "218",
          supplier_name: "测试供应商3"
        }
      ],
      customers: [
        {
          customer_id: Math.floor(Math.random() * 5) + 1,
          project_id: Math.floor(Math.random() * 3) + 1
        },
        {
          customer_id: Math.floor(Math.random() * 5) + 1,
          project_id: Math.floor(Math.random() * 3) + 1
        }
      ],
      variable_rates: [
        {
          date_max: 30,
          rate: (Math.random() * 2 + 1).toFixed(2)
        },
        {
          date_max: 60,
          rate: (Math.random() * 2 + 2).toFixed(2)
        },
        {
          date_max: 90,
          rate: (Math.random() * 2 + 3).toFixed(2)
        }
      ]
    }
    
// 确保变动利率数据正确传递
    // 创建一个明确包含所有必要字段的对象，确保变动利率字段和create_id正确命名
    const finalData = {
      ...contractData,
      createId: contractData.create_id,
      isMeasured: contractData.is_measured,
      fixedRate: contractData.fixed_rate,
      price_method: contractData.pricing_method,
      variableRates: contractData.variable_rates,
      variable_rates: contractData.variable_rates
    }
    
    // 打印最终提交的数据以便调试
try {
      const response = await save_customer_contract(finalData)
return response
    } catch (error) {
      console.error('创建变动利率客户合同请求失败:', error)
      
      // 尝试直接使用http模块
try {
        const directResponse = await http.post('/customer-contracts', finalData)
return directResponse
      } catch (httpError) {
        console.error('直接HTTP请求也失败:', httpError)
        throw httpError
      }
    }
  } catch (error) {
    console.error('创建变动利率客户合同失败:', error)
    return Promise.reject({
      data: {
        success: false,
        message: '创建变动利率客户合同失败: ' + (error.message || '未知错误'),
        error: error.toString(),
        stack: error.stack
      }
    })
  }
}

/**
 * 随机更新一个客户合同
 * @description 从现有客户合同中随机选择一个，并根据其是固定利率还是变动利率进行相应的更新
 * @returns {Promise} 返回更新结果
 */
export async function update_random_customer_contract() {
  try {
    // 1. 获取所有客户合同
const contractsResponse = await query_customer_contracts_pagination()
// 2. 从响应中提取合同列表
    let contractsList = []
    
    // 尝试各种可能的数据结构
    try {
      if (contractsResponse.data && contractsResponse.data.data && Array.isArray(contractsResponse.data.data.data)) {
contractsList = contractsResponse.data.data.data
      } else if (contractsResponse.data && contractsResponse.data.data && Array.isArray(contractsResponse.data.data)) {
contractsList = contractsResponse.data.data
      } else if (contractsResponse.data && Array.isArray(contractsResponse.data.items)) {
contractsList = contractsResponse.data.items
      } else if (Array.isArray(contractsResponse.data)) {
contractsList = contractsResponse.data
      } else {
        // 如果没有找到有效数据，创建一个测试合同再更新
const newContract = await create_random_fixed_rate_customer_contract()
        if (newContract.data && newContract.data.data) {
          contractsList = [newContract.data.data]
        } else if (newContract.data) {
          contractsList = [newContract.data]
        } else {
          throw new Error('无法创建测试客户合同')
        }
      }
    } catch (parseError) {
      console.error('解析客户合同数据出错:', parseError)
      throw parseError
    }
    
// 3. 确保有可用的合同
    if (!contractsList || !contractsList.length) {
      console.error('没有可用的客户合同信息')
      return Promise.reject({
        data: {
          success: false,
          message: '没有可用的客户合同信息，无法进行更新',
          details: contractsResponse && contractsResponse.data ? JSON.stringify(contractsResponse.data) : '无响应数据'
        }
      })
    }
    
    // 4. 随机选择一个合同
    const randomContract = contractsList[Math.floor(Math.random() * contractsList.length)]
// 5. 获取合同详情以便更新
    const contractDetail = await query_customer_contract_by_id(randomContract.id)
    const contractData = contractDetail.data.data || contractDetail.data
// 6. 检查是固定利率还是变动利率合同
    const isFixedRate = contractData.fixedRate !== "0" && contractData.fixedRate !== 0
// 7. 根据合同类型构建更新数据
    const updateData = {
      id: contractData.id,
      contract_name: contractData.contract_name + '_已更新',
      isMeasured: contractData.isMeasured,
      interestfree_maxday: contractData.interestfree_maxday,
      settlement_method: contractData.settlement_method,
      payment_method: contractData.payment_method,
      price_method: contractData.price_method,
      status: contractData.status
    }
    
    // 根据合同类型更新相应的利率数据
    if (isFixedRate) {
      // 如果是固定利率合同，随机更新固定利率
      updateData.fixedRate = (parseFloat(contractData.fixedRate) + Math.random()).toFixed(2)
    } else {
      // 如果是变动利率合同，保持fixedRate为0，更新变动利率
      updateData.fixedRate = "0"
      
      // 提取或创建变动利率数组
      let variableRates = []
      if (contractData.variable_rates && Array.isArray(contractData.variable_rates)) {
        variableRates = [...contractData.variable_rates]
      } else if (contractData.variableRates && Array.isArray(contractData.variableRates)) {
        variableRates = [...contractData.variableRates]
      } else {
        // 如果没有变动利率数据，创建新的
        variableRates = [
          { days: 30, rate: "1.5" },
          { days: 60, rate: "2.5" },
          { days: 90, rate: "3.5" }
        ]
      }
      
      // 随机更新变动利率
      variableRates = variableRates.map(item => ({
        ...item,
        rate: (parseFloat(item.rate) + Math.random()).toFixed(2)
      }))
      
      updateData.variable_rates = variableRates
    }
    
    // 添加材料列表（如果有）
    if (contractData.materials && Array.isArray(contractData.materials)) {
      updateData.materials = contractData.materials
    } else if (contractData.contractItems && Array.isArray(contractData.contractItems)) {
      updateData.materials = contractData.contractItems
    }
    
    // 添加客户关系（如果有）
    if (contractData.customers && Array.isArray(contractData.customers)) {
      updateData.customers = contractData.customers
    } else if (contractData.customerProjects && Array.isArray(contractData.customerProjects)) {
      updateData.customers = contractData.customerProjects
    }
    
// 8. 更新合同
    return save_customer_contract(updateData)
  } catch (error) {
    console.error('更新随机客户合同失败:', error)
    return Promise.reject({
      data: {
        success: false,
        message: '更新客户合同失败: ' + (error.message || '未知错误'),
        error: error.toString(),
        stack: error.stack
      }
    })
  }
}

/**
 * 删除指定ID的客户合同
 * @param {Number|String} id - 客户合同ID
 * @returns {Promise} 返回删除结果
 * @description 软删除操作，将合同的deleted字段标记为true
 */
export function del_customer_contract_by_id(id) {
return http.delete(`/customer-contracts/${id}`)
}

/**
 * 分页查询合同列表（统一接口）
 * @param {Object} params 查询参数
 * @param {String} [params.contract_name] - 合同名称(可选，支持模糊查询)
 * @param {Number} [params.supplier_id] - 供应商ID(可选，精确匹配)
 * @param {Number} [params.customer_id] - 客户ID(可选，精确匹配)
 * @param {Number} [params.create_id] - 创建者ID(可选，精确匹配)
 * @param {String} [params.status] - 合同状态(可选)
 * @param {Number} [params.page] - 当前页码(从0开始，默认为0)
 * @param {Number} [params.size] - 每页记录数(可选，默认为10)
 * @returns {Promise} 返回合同信息分页列表
 */
export function query_contracts(params = {}) {
  return http.get('/contracts/page', { params })
}

/**
 * 创建或更新合同（统一接口）
 * @param {Object} contractData 合同数据
 * @param {Number} [contractData.id] - 合同ID（更新时必填，创建时不填）
 * @param {String} contractData.contractName - 合同名称
 * @param {String} [contractData.contractNumber] - 合同编号
 * @param {String} [contractData.contractDate] - 合同日期
 * @param {Number} [contractData.contractAmount] - 合同金额
 * @param {String} contractData.contractType - 合同类型（SALES/PURCHASE）
 * @param {String} [contractData.pricingMethod] - 定价方式
 * @param {Boolean} [contractData.interestCalculation] - 是否计息
 * @param {String} [contractData.settlementMethod] - 结算方式
 * @param {Number} [contractData.payment70PercentDays] - 70%付款天数
 * @param {Number} [contractData.paymentFullDays] - 全额付款天数
 * @param {Number} [contractData.interestFreeDays] - 免息天数
 * @param {String} [contractData.interestRateType] - 利率类型
 * @param {Number} [contractData.fixedInterestRate] - 固定利率
 * @param {Array} [contractData.contractParties] - 合同参与方列表
 * @param {Array} [contractData.contractItems] - 合同明细列表
 * @returns {Promise} 返回创建或更新结果
 */
export function saveContract(contractData) {
return http.post('/contracts', contractData);
}

/**
 * 分页根据条件查询采购合同
 * @param {Object} params 查询参数
 * @param {String} [params.contractName] - 合同名称（模糊查询，可选）
 * @param {String} [params.contractNumber] - 合同编号（模糊查询，可选）
 * @param {String} [params.contractType] - 合同类型（SALES/PURCHASE，可选）
 * @param {Number} [params.creatorId] - 创建人ID（可选）
 * @param {String} [params.status] - 合同状态（可选）
 * @param {Number} [params.page] - 页码，默认为0（后端从0开始）
 * @param {Number} [params.size] - 每页数量，默认为10
 * @param {String} [params.sortBy] - 排序字段，默认为createTime
 * @param {String} [params.sortDir] - 排序方向（asc/desc），默认为desc
 * @returns {Promise} 返回采购合同信息列表和分页信息
 * @description 系统会自动过滤，只返回需求方单位（demandUnits）包含当前登录用户所属单位的合同
 */
export function query_purchase_contracts_pagination(params = {}) {
// 构建查询参数，前端页码从1开始，后端从0开始
  const queryParams = {
    page: params.page !== undefined ? (params.page - 1) : 0, // 转换为后端的0基页码
    size: params.size || 10,
    sortBy: params.sortBy || 'createTime',
    sortDir: params.sortDir || 'desc'
  }
  
  // 添加可选查询参数
  if (params.contractName) {
    queryParams.contractName = params.contractName
  }
  if (params.contractNumber) {
    queryParams.contractNumber = params.contractNumber
  }
  if (params.contractType) {
    queryParams.contractType = params.contractType
  }
  if (params.creatorId) {
    queryParams.creatorId = params.creatorId
  }
  if (params.status) {
    queryParams.status = params.status
  }
  
  return http.get('/contracts/purchase/search', { params: queryParams })
}

/**
 * 查询指定单位的所有采购合同（按状态筛选）
 * @param {number|string} unitId - 单位ID
 * @param {Object} params - 查询参数
 * @param {string} [params.status] - 合同状态，如'EXECUTING'表示执行中的合同
 * @returns {Promise} 返回采购合同列表
 */
export function query_purchase_contracts_all(unitId, params = {}) {
return http.get(`/contracts/purchase/unit/${unitId}/all`, { params })
}

/**
 * 根据ID查询合同详情（新的统一API）
 * @param {number|string} id - 合同ID
 * @returns {Promise} 返回合同详情信息
 */
export function getContractById(id) {
return http.get(`/contracts/${id}`);
}

export function check_contract_item_deletion(contractId, itemId) {
return http.get(`/contracts/${contractId}/items/${itemId}/deletion-check`);
}

/**
 * 分页根据条件查询销售合同
 * @param {Object} params 查询参数
 * @param {String} [params.contractName] - 合同名称（模糊查询，可选）
 * @param {String} [params.contractNumber] - 合同编号（模糊查询，可选）
 * @param {String} [params.contractType] - 合同类型（SALES/PURCHASE，可选）
 * @param {Number} [params.creatorId] - 创建人ID（可选）
 * @param {String} [params.status] - 合同状态（可选）
 * @param {Number} [params.page] - 页码，默认为0（后端从0开始）
 * @param {Number} [params.size] - 每页数量，默认为10
 * @param {String} [params.sortBy] - 排序字段，默认为createTime
 * @param {String} [params.sortDir] - 排序方向（asc/desc），默认为desc
 * @returns {Promise} 返回销售合同信息列表和分页信息
 * @description 系统会自动过滤，只返回供方单位（supplierUnit）为当前登录用户所属单位的合同
 */
export function query_sales_contracts_pagination(params = {}) {
// 构建查询参数，前端页码从1开始，后端从0开始
  const queryParams = {
    page: params.page !== undefined ? (params.page - 1) : 0, // 转换为后端的0基页码
    size: params.size || 10,
    sortBy: params.sortBy || 'createTime',
    sortDir: params.sortDir || 'desc'
  }
  
  // 添加可选查询参数
  if (params.contractName) {
    queryParams.contractName = params.contractName
  }
  if (params.contractNumber) {
    queryParams.contractNumber = params.contractNumber
  }
  if (params.contractType) {
    queryParams.contractType = params.contractType
  }
  if (params.creatorId) {
    queryParams.creatorId = params.creatorId
  }
  if (params.status) {
    queryParams.status = params.status
  }
  
  return http.get('/contracts/sales/search', { params: queryParams })
}

/**
 * 更新合同状态
 * @param {Number|String} id - 合同ID
 * @param {String} status - 合同状态
 * @returns {Promise} 返回更新结果
 */
export function updateContractStatus(id, status) {
  return http.put(`/contracts/${id}/status?status=${status}`)
}

export function reviewContract(id, approved, remark) {
  return http.put(`/contracts/${id}/review`, null, { params: { approved, remark } })
}

export function getContractReviewers(id) {
  return http.get(`/contracts/${id}/reviewers`)
}

/**
 * 更新合同的关联项目
 * @param {Number|String} id - 合同ID
 * @param {Array<Number>} projectIds - 项目ID数组
 * @returns {Promise} 返回更新结果
 */
export function updateContractProjects(id, projectIds) {
return http.put(`/contracts/${id}/projects`, { projectIds });
}

export default {
  query_customer_contracts_pagination,
  query_customer_contract_by_id,
  query_supplier_contracts,
  query_supplier_contract_by_id,
  save_supplier_contract,
  create_random_fixed_rate_contract,
  create_random_variable_rate_contract,
  update_random_contract,
  del_supplier_contract_by_id,
  save_customer_contract,
  create_random_fixed_rate_customer_contract,
  create_random_variable_rate_customer_contract,
  update_random_customer_contract,
  del_customer_contract_by_id,
  query_contracts,
  query_purchase_contracts_pagination,
  query_purchase_contracts_all,
  query_sales_contracts_pagination,
  saveContract,
  getContractById,
  updateContractStatus,
  reviewContract,
  getContractReviewers,
  updateContractProjects
}
